import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { auth, provider, db } from "../config/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import {
  RenderMenu,
  RenderRoutes,
} from "../components/Navigation/RenderNavigation";
import { SideMenuContext } from "../context/SideMenuContext";
import { SideMenuProvider } from "../context/SideMenuContext";
import { CurrencyDataProvider } from "../context/CurrencyDataContext";
import { NotificationProvider } from "../context/NotificationContext";

// Хэрэглэгчийн нэвтрэлтийн мэдээллийг хадгалах Context үүсгэх
const AuthContext = createContext();
export const AuthData = () => useContext(AuthContext);
// Аппликейшний authentication wrapper component
// Энэ нь бүх хэрэглэгчийн нэвтрэлттэй холбоотой үйлдлүүдийг удирдана
export const AuthWrapper = ({ children }) => {
  const navigate = useNavigate();

  // Хэрэглэгчийн мэдээллийг SDK дотоод төлөвт тулгуурлан удирдана (localStorage ашиглахгүй)
  const [user, setUser] = useState({ name: "", isAuthenticated: false });

  // Firestore дээр хадгалагдах хэрэглэгчийн профиль (роль, зөвшөөрөл, статус)
  const [userProfile, setUserProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Токенг гар аргаар хадгалах/шинэчлэх шаардлагагүй (Firebase SDK өөрөө менеж хийдэг)

  // Firestore дээр хэрэглэгчийн бичлэг байгаа эсэхийг шалгаж, байхгүй бол PENDING төлөвтэй үүсгэх
  const ensureUserDocument = async (firebaseUser) => {
    if (!firebaseUser) return null;
    const { uid, email, displayName, photoURL } = firebaseUser;
    const userRef = doc(db, "users", uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      await setDoc(userRef, {
        uid,
        email: email || "",
        displayName: displayName || "",
        photoURL: photoURL || "",
        status: "PENDING",
        roles: ["PENDING"],
        allowedRoutes: [],
        read: [],
        write: [],
        rtdbRead: [],
        rtdbWrite: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      // Анхны супер-админ бол шууд идэвхжүүлнэ
      if (email === "abatod@gmail.com") {
        await setDoc(
          userRef,
          {
            status: "ACTIVE",
            roles: ["SUPER_ADMIN"],
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      return (await getDoc(userRef)).data();
    }
    const data = snap.data();
    if (
      email === "abatod@gmail.com" &&
      (data.status !== "ACTIVE" ||
        !(Array.isArray(data.roles) && data.roles.includes("SUPER_ADMIN")))
    ) {
      await setDoc(
        userRef,
        {
          status: "ACTIVE",
          roles: ["SUPER_ADMIN"],
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      return (await getDoc(userRef)).data();
    }
    return data;
  };

  // Firebase-н хэрэглэгчийн төлөв өөрчлөгдөхийг сонсож, user/userProfile татах
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser({ name: "", isAuthenticated: false });
        setUserProfile(null);
        setIsAuthReady(true);
        return;
      }
      try {
        setIsLoadingProfile(true);
        setUser({
          name: firebaseUser.displayName || "",
          isAuthenticated: true,
        });
        const profile = await ensureUserDocument(firebaseUser);
        setUserProfile(profile);
      } catch (e) {
        console.error("Failed to load user profile", e);
      } finally {
        setIsLoadingProfile(false);
        setIsAuthReady(true);
      }
    });
    return () => unsubscribe();
  }, []);

  // Google-р нэвтрэх функц
  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, provider);
    // UI-г шуурхай болгохын тулд хэрэглэгчийн нэрийг state-д хадгална (токен хадгалахгүй)
    setUser({ name: result.user.displayName || "", isAuthenticated: true });
    // Профайлыг үүсгээд татна (pending байж болно)
    try {
      setIsLoadingProfile(true);
      const profile = await ensureUserDocument(result.user);
      setUserProfile(profile);
    } catch (e) {
      console.error("Failed to ensure user doc", e);
    } finally {
      setIsLoadingProfile(false);
    }
    navigate("/home");
  };

  // Системээс гарах hook
  const useFirebaseSignOut = () => {
    const [isSigningOut, setIsSigningOut] = useState(false);
    const [error, setError] = useState(null);
    const { closeMenu } = useContext(SideMenuContext);

    const navigate = useNavigate();
    // Гарах үйлдлийг гүйцэтгэх функц
    const signOutUser = async () => {
      try {
        await signOut(auth);
        setIsSigningOut(true);
        closeMenu();
        setUser({ name: "", isAuthenticated: false });
        setUserProfile(null);
        navigate("/");
      } catch (error) {
        setError(error);
      } finally {
        setIsSigningOut(false);
      }
    };

    return { signOutUser, isSigningOut, error };
  };

  // Authentication context provider-г буцаах
  // Энэ нь дараах provider-уудыг агуулна:
  // - NotificationProvider: Мэдэгдлүүдийг удирдах
  // - SideMenuProvider: Хажуугийн цэсийг удирдах
  // - CurrencyDataProvider: Валютын мэдээллийг удирдах
  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        isAuthReady,
        isLoadingProfile,
        signInWithGoogle,
        useFirebaseSignOut,
      }}
    >
      <NotificationProvider>
        <SideMenuProvider>
          <CurrencyDataProvider>
            <RenderMenu />
            <RenderRoutes />
          </CurrencyDataProvider>
        </SideMenuProvider>
      </NotificationProvider>
    </AuthContext.Provider>
  );
};
